import React, { useCallback, useEffect } from 'react';
import { ensureJwtAccessTokenRefreshed } from '../api/jwtAuth';
import { addDays } from 'date-fns';
import { resetPostHogUser } from '../initializers/posthog';
import { checkHasValidRefreshToken } from '../api/jwtAuth/jwtAuthApi';

type Auth = {
  isLoggedIn: boolean | undefined;
  isServerError: boolean | undefined;
  serverErrorMessage?: string;
  isLoading: boolean;
  login: (apiToken: string, tokenType: ApiTokenType) => void;
  logout: () => void;
};

/* eslint-disable no-unused-vars */
export enum ApiTokenType {
  JWT,
  LEGACY,
}
/* eslint-enable no-unused-vars */

const AuthenticatedContext = React.createContext<Auth>({
  isLoggedIn: undefined,
  isServerError: undefined,
  isLoading: true,
  login: () => {},
  logout: () => {},
});

const API_TOKEN_STORAGE_KEY_LEGACY = 'flairEmployeeHubApiToken';
const API_DOCUMENTS_TOKEN_COOKIE_LEGACY = 'flairDocumentsToken';

export const ensureApiJwtTokenRefreshed = async (): Promise<string | null> => {
  const apiToken = getApiToken();
  if (!isApiTokenJwt(apiToken)) {
    return null;
  }
  const jwtToken = apiToken?.split(' ')[1];
  if (!jwtToken) {
    return null;
  }
  const refreshResult = await ensureJwtAccessTokenRefreshed(jwtToken);
  if (refreshResult.isRefreshed) {
    saveApiToken(`Bearer ${refreshResult.jwtAccessToken}`, ApiTokenType.JWT);
  }
  return `Bearer ${refreshResult.jwtAccessToken}`;
};

const getApiTokenLegacy = (): string | null => {
  return localStorage.getItem(API_TOKEN_STORAGE_KEY_LEGACY);
};

const saveApiTokenLegacy = (apiToken: string | null) => {
  if (apiToken === null) {
    localStorage.removeItem(API_TOKEN_STORAGE_KEY_LEGACY);
    deleteTempFallbackCookieLegacy();
    return;
  }
  if (isApiTokenLegacy(apiToken)) {
    localStorage.setItem(API_TOKEN_STORAGE_KEY_LEGACY, apiToken);
    createTempFallbackCookieLegacy(apiToken);
  }
};

let apiTokenInMemory: string | null = getApiTokenLegacy();

export const getApiToken = (): string | null => {
  return apiTokenInMemory;
};

export const getApiTokenType = (apiToken: string): ApiTokenType => {
  if (apiToken.startsWith('Bearer ')) {
    return ApiTokenType.JWT;
  }
  return ApiTokenType.LEGACY;
};

export const isApiTokenLegacy = (apiToken: string | null): boolean => {
  return apiToken !== null && getApiTokenType(apiToken) === ApiTokenType.LEGACY;
};

export const isApiTokenJwt = (apiToken: string | null): boolean => {
  return apiToken !== null && getApiTokenType(apiToken) === ApiTokenType.JWT;
};

export const saveApiToken = (
  apiToken: string | null,
  tokenType: ApiTokenType,
) => {
  if (apiToken && getApiTokenType(apiToken) !== tokenType) {
    throw new Error('Invalid token type: ' + tokenType);
  }
  apiTokenInMemory = apiToken;
  saveApiTokenLegacy(apiToken);
  if (isApiTokenJwt(apiToken)) {
    // let's remove token from local storage to be on the safe side
    localStorage.removeItem(API_TOKEN_STORAGE_KEY_LEGACY);
    return;
  }
};

type AuthLoggedInState = {
  isLoggedIn: boolean | undefined;
  isServerError: boolean | undefined;
  isLoading: boolean;
  serverErrorMessage?: string;
};

export const AuthProvider: React.FC = (props) => {
  const [authState, setAuthState] = React.useState<AuthLoggedInState>({
    isLoggedIn: undefined,
    isLoading: true,
    isServerError: undefined,
  });

  // run at start to check if the browser has a valid cookie with refresh token
  useEffect(() => {
    const apiToken = getApiToken();
    // todo: this is legacy way. Remove it
    if (apiToken && getApiTokenType(apiToken) === ApiTokenType.LEGACY) {
      setAuthState({
        isLoggedIn: true,
        isLoading: false,
        isServerError: false,
      });
      return;
    }
    // end of remove legacy way
    // we need to check if the browser has a valid cookie with refresh token
    checkHasValidRefreshToken()
      .then((checkResponse) => {
        if (checkResponse.status === 'noToken') {
          // it means no jwt or legacy api token is set
          setAuthState({
            isLoggedIn: false,
            isLoading: false,
            isServerError: false,
          });
        } else if (checkResponse.status === 'hasToken') {
          // it means we have a valid jwt
          setAuthState({
            isLoggedIn: true,
            isLoading: false,
            isServerError: false,
          });
          // for web sockets
          saveApiToken('Bearer ' + checkResponse.accessToken, ApiTokenType.JWT);
        } else if (checkResponse.status === 'error') {
          setAuthState({
            isLoggedIn: false,
            isLoading: false,
            isServerError: true,
            serverErrorMessage: checkResponse.errorMessage,
          });
        }
      })
      .catch((error) => {
        setAuthState({
          isLoggedIn: false,
          isLoading: false,
          isServerError: true,
          serverErrorMessage: 'Internal error: ' + error,
        });
      });
  }, [setAuthState]);

  useEffect(() => {
    const apiToken = getApiToken();
    if (
      authState.isLoggedIn === true &&
      apiToken &&
      isApiTokenLegacy(apiToken)
    ) {
      createTempFallbackCookieLegacy(apiToken);
    }
  }, [authState]);

  const logout = () => {
    // Reset PostHog user identification on logout
    resetPostHogUser();
    saveApiToken(null, ApiTokenType.JWT);
    setAuthState({ isLoggedIn: false, isLoading: false, isServerError: false });
  };

  const login = useCallback(
    (apiToken: string, tokenType: ApiTokenType) => {
      saveApiToken(apiToken, tokenType);
      setAuthState({
        isLoggedIn: true,
        isLoading: false,
        isServerError: false,
      });
    },
    [setAuthState],
  );

  return (
    <AuthenticatedContext.Provider
      value={{
        isLoggedIn: authState.isLoggedIn,
        isLoading: authState.isLoading,
        isServerError: authState.isServerError,
        serverErrorMessage: authState.serverErrorMessage,
        login,
        logout,
      }}
      {...props}
    />
  );
};

export const useAuth = (): Auth => React.useContext(AuthenticatedContext);

const deleteTempFallbackCookieLegacy = () => {
  document.cookie = `${API_DOCUMENTS_TOKEN_COOKIE_LEGACY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/api/documents/`;
};

const createTempFallbackCookieLegacy = (apiToken: string) => {
  const expire = addDays(new Date(), 100);
  const encodedToken = encodeURIComponent(apiToken);
  document.cookie = `${API_DOCUMENTS_TOKEN_COOKIE_LEGACY}=${encodedToken};expires=${expire.toUTCString()};secure;path=/api/documents/`;
};
